"use client";

// Auth context — holds the access token + current user. Wraps the app
// at the root so any hook can read the user / login / logout.
//
// Bootstrapping: on mount, if there's no token in memory we try
// /auth/refresh once (the refresh cookie may still be valid from a prior
// session). Either way we always settle into a deterministic loading
// state before rendering protected pages.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import {
  apiGet,
  apiPost,
  setAccessToken,
  onAuthLostCallback,
  ApiError,
} from "./api";
import { pushToast } from "./notify";
import { clearViewAsCustomer } from "./view-as";
import { canAccessPath, homePathFor } from "./rbac";
import type { LoginResponseData, UserPublic } from "./types";

interface AuthContextValue {
  user: UserPublic | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<UserPublic>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const PUBLIC_PATHS = new Set(["/login", "/reset-password"]);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const [user, setUser] = useState<UserPublic | null>(null);
  const [loading, setLoading] = useState(true);

  // Bootstrap: try to recover an existing session via refresh cookie.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const env = await apiPost<{ access_token: string }>("/auth/refresh");
        if (cancelled) return;
        setAccessToken(env.access_token);
        const me = await apiGet<UserPublic>("/auth/me");
        if (!cancelled) setUser(me);
      } catch {
        // No session — that's OK, we just show login.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // If a refresh fails later, kick the user back to login + toast.
  useEffect(() => {
    onAuthLostCallback(() => {
      setAccessToken(null);
      setUser(null);
      pushToast("warning", "เซสชันหมดอายุ — กรุณาเข้าสู่ระบบใหม่");
      router.replace("/login");
    });
  }, [router]);

  // Route guard: unauthenticated → /login; signed-in on a public page →
  // role home; signed-in on a page outside the role's ROUTE_ACCESS table
  // (hand-edited URL into another role's pages) → role home + toast. The
  // page is also not rendered in that last case (see `blocked` below) so it
  // never mounts and never fires its data requests.
  useEffect(() => {
    if (loading) return;
    const isPublic = PUBLIC_PATHS.has(pathname || "");
    if (!user && !isPublic) {
      router.replace("/login");
    } else if (user && isPublic) {
      router.replace(homePathFor(user.role));
    } else if (user && !canAccessPath(user.role, pathname)) {
      pushToast("warning", "บัญชีของคุณไม่มีสิทธิ์เข้าถึงหน้านี้");
      router.replace(homePathFor(user.role));
    }
  }, [loading, user, pathname, router]);

  const login = useCallback(async (email: string, password: string) => {
    const data = await apiPost<LoginResponseData>("/auth/login", { email, password });
    setAccessToken(data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await apiPost("/auth/logout"); } catch { /* ignore */ }
    setAccessToken(null);
    setUser(null);
    clearViewAsCustomer();
    router.replace("/login");
  }, [router]);

  const refresh = useCallback(async () => {
    const me = await apiGet<UserPublic>("/auth/me");
    setUser(me);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, login, logout, refresh }),
    [user, loading, login, logout, refresh],
  );

  // Hold rendering of protected pages until the bootstrap refresh
  // settles. Without this, SWR fetchers in protected pages fire on
  // mount before the access token is ready → they 401 → the axios
  // interceptor calls /auth/refresh while AuthProvider is *also*
  // refreshing → two refresh requests with the same refresh-token
  // cookie → backend reuse-detection revokes the family → user is
  // forced back to /login on every F5. Public pages still render
  // (login form needs to mount even while loading=true).
  const isPublic = PUBLIC_PATHS.has(pathname || "");
  const blocked = !loading && !!user && !isPublic && !canAccessPath(user.role, pathname);
  if ((loading && !isPublic) || blocked) {
    return (
      <AuthContext.Provider value={value}>
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-3)", fontSize: 13 }}>
          <span>{blocked ? "ไม่มีสิทธิ์เข้าถึงหน้านี้ — กำลังพากลับหน้าหลัก…" : "กำลังตรวจสอบเซสชัน…"}</span>
        </div>
      </AuthContext.Provider>
    );
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

export type { ApiError };
